import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { CrossSellingModalConfig } from '@front-platform/content-builder';
import { crossSellingModalResolveConfig, resolveCloseIconColors } from '@front-platform/content-builder';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ProductStoreDispatcher } from '@common/dispatchers/product.dispatcher';
import { OrderStoreSelector } from '@common/selectors/order.selector';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import {
  KIOSK_PRODUCT_OVERVIEW_ABOVE_SALES_BOOSTER_INDEX,
  KioskProductOverviewModalComponent,
  KioskProductOverviewModalConfig,
} from '@shared/modals/kiosk-product-overview-modal/kiosk-product-overview-modal.component';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { DoodProductModel } from '@core/models/product.model';
import { CB2_PREVIEW_BLOC_KEY } from '@core/services/cb2-preview/cb2-preview-bloc-key.token';
import { Cb2PreviewStateService } from '@core/services/cb2-preview/cb2-preview-state.service';
import { CloseModalOnBrowserBack } from '@core/services/modal-back-history/modal-close-on-back.decorator';
import { GetModalInterface, ModalConfig } from '@core/services/modals/modals-util.type';
import { ModalsService } from '@core/services/modals/modals.service';
import { Store } from '@ngrx/store';
import { ContentBuilderFieldColors } from '@shared/interfaces/content-builder.interface';
import { TranslationField } from '@shared/interfaces/translation-field.interface';
import { KioskGlobalSettingsService } from '@store/kiosk-global-settings/kiosk-global-settings.service';
import { PRODUCT_STORE_COMPONENT_ACTIONS } from '@store/product';
import { map } from 'rxjs/operators';
import { resolveCrossSellingModalProducts } from './cross-selling-modal-preview.util';
import { resolveCrossSellingProductsLayout } from './cross-selling-products-layout';

export type CrossSellingModalModalConfig = ModalConfig<{
  handle: typeof CrossSellingModalComponent.handle;
  inputs: {
    triggerProductId: string;
    crossSellingRuleId: string;
    products: DoodProductModel[];
    title?: TranslationField;
    description?: TranslationField;
    colors?: {
      background_color?: IContentBuilderFieldColor;
      heading_color?: IContentBuilderFieldColor;
      description_color?: IContentBuilderFieldColor;
      productCardColors?: {
        textColor?: IContentBuilderFieldColor;
        cardColor?: IContentBuilderFieldColor;
        buttonBackgroundColor?: IContentBuilderFieldColor;
        unavailableButtonBackground?: IContentBuilderFieldColor;
        unavailableButtonText?: IContentBuilderFieldColor;
        buttonTextColor?: IContentBuilderFieldColor;
      };
      card_color?: IContentBuilderFieldColor;
      close_icon_colors?: ContentBuilderFieldColors;
    };
  };
}>;
export interface CrossSellingModalRuntimeConfig {
  'trigger_product_id'?: string;
  'products'?: DoodProductModel[];
  'cross_selling_rule_id'?: string;
}

export type CrossSellingModalConfigInput = Partial<
  Omit<CrossSellingModalConfig, keyof CrossSellingModalRuntimeConfig> &
    CrossSellingModalRuntimeConfig
>;
export type CrossSellingModalResolvedConfig = Omit<
  CrossSellingModalConfig,
  keyof CrossSellingModalRuntimeConfig
> &
  Partial<CrossSellingModalRuntimeConfig>;
export interface CrossSellingModalContract extends ConfigComponentContract<
  CrossSellingModalConfigInput,
  CrossSellingModalResolvedConfig
> {}

@CloseModalOnBrowserBack()
@Component({
  selector: 'app-cross-selling-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  styles: [
    `
      :host {
        --grid-column-width: 240px;
      }
    `,
  ],
  host: {
    'data-testid': 'cross-selling-container',
  },
  template: `
    <app-modal-filler-atom
      (onClick)="close()"
      [index]="5"
    ></app-modal-filler-atom>
    <div
      class="fixed bottom-0 left-0 right-0 mx-auto w-full md:inset-0 md:flex md:items-center md:justify-center md:p-4"
      [style.z-index]="750"
    >
      @if (isDesktop()) {
        <app-modal-filler-atom
          [withBackdrop]="false"
          (onClick)="close()"
          [index]="index()"
        ></app-modal-filler-atom>
      }
      <div
        class="flex max-h-85-screen w-full flex-col gap-6 overflow-y-auto rounded-t-3xl bg-white py-8 text-center md:max-h-[90vh] md:rounded-3xl lg:p-5 kiosk:px-16 kiosk-xl:max-w-[63rem]"
        [class.md:max-w-2xl]="!productsLayout().wideModal"
        [class.md:max-w-4xl]="productsLayout().wideModal"
        [style.z-index]="index() + 50"
        [style.backgroundColor]="backgroundColor() | contentBuilderColor: '#ffffff'"
      >
        <div class="space-y-5 px-4.5 py-5">
          <div class="flex items-center">
            <div
              class="flex flex-1 items-center"
              [style.color]="headingColor() | contentBuilderColor: '#000'"
            >
              <span class="font-base font-bold lg:text-md kiosk:text-3.5xl">{{
                title()
                  | translateField
                    : {
                        defaultTranslationKey: 'crossSelling.modal-title',
                      }
              }}</span>
            </div>
            <button
              (click)="close()"
              class="icon icon-cross h-8 w-8 rounded-full text-xs kiosk:h-16 kiosk:w-16 kiosk:text-3xl"
              [style.backgroundColor]="
                closeIconFieldColors()?.background
                  | contentBuilderColor: 'var(--ui-color-primary-200)'
              "
              [style.color]="
                closeIconFieldColors()?.font | contentBuilderColor: 'var(--ui-color-primary-900)'
              "
            ></button>
          </div>

          <!-- Description -->
          @if (description()) {
            <div
              class="text-left"
              [style.color]="descriptionColor() | contentBuilderColor: '#000'"
            >
              <p class="font-medium lg:text-sm kiosk:text-2.5xl">
                {{ description() | translateField }}
              </p>
            </div>
          }

          <div class="flex flex-col gap-5 overflow-scroll scrollbar-hide">
            @let layout = productsLayout();
            <div
              data-testid="cross-selling-products"
              [class]="layout.listClass"
              [style.gridTemplateColumns]="layout.gridTemplateColumns"
            >
              @for (product of products(); track product.id; let index = $index) {
                <div
                  class="flex min-w-0 flex-col gap-3"
                  [class.flex-1]="layout.stretchCard"
                  [class.mx-auto]="layout.centerSingleCard"
                  [class.w-full]="layout.fillCardWidth"
                  [class.max-w-[365px]]="layout.centerSingleCard"
                >
                  <div class="flex flex-1 flex-col">
                    @if (deviceIsKiosk() || !isMobile()) {
                      @if (product) {
                        <app-kiosk-product-card-factory
                          [product]="product"
                          [quantity]="product.quantity"
                          (addProduct)="addProductDispatcher(product)"
                          (showProduct)="openProductModal(product)"
                          [textColor]="textColor()"
                          [cardColor]="cardColor()"
                          [isProductAvailable]="
                            !!product.available && !unAvailableProductById()[product.id]
                          "
                          [buttonBackgroundColor]="buttonBackgroundColor()"
                          [unavailableButtonBackground]="unavailableButtonBackground()"
                          [unavailableButtonText]="unavailableButtonText()"
                          [buttonTextColor]="buttonTextColor()"
                          class="flex-1"
                        ></app-kiosk-product-card-factory>
                      }
                    } @else {
                      <app-product-card-atom
                        [product]="product"
                        (productClick)="addProductDispatcher(product)"
                        class="flex-1"
                      />
                    }
                  </div>
                </div>
              }
            </div>
          </div>
        </div>
        <div class="pt-3 shadow-top md:pt-0 md:shadow-none">
          <div class="px-4.5 md:px-0">
            <button
              type="button"
              data-testid="cross-selling-decline-button"
              class="w-full py-3 text-center text-sm font-semibold text-neutral-700 underline-offset-2 hover:underline kiosk:py-5 kiosk:text-2xl"
              (click)="close()"
            >
              {{ 'crossSelling.no-thank-you' | translate }}
            </button>
          </div>
        </div>
      </div>
    </div>
  `,
})
export class CrossSellingModalComponent
  implements GetModalInterface<CrossSellingModalModalConfig>, CrossSellingModalContract
{
  readonly config = input<CrossSellingModalConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    CrossSellingModalConfig,
    CrossSellingModalRuntimeConfig
  >(crossSellingModalResolveConfig, () => ({ config: this.config() }));
  static handle = 'cross-selling-modal' as const;

  private readonly settingsStoreSelector = inject(SettingsStoreSelector);
  private readonly productDispatcher = inject(ProductStoreDispatcher);
  private readonly orderStoreSelector = inject(OrderStoreSelector);
  private readonly modalsService: ModalsService<CrossSellingModalModalConfig> = inject(
    ModalsService<CrossSellingModalModalConfig>,
  );
  protected readonly isDesktop = toSignal(this.settingsStoreSelector.selectDeviceIsDesktopScreen);
  protected readonly isKiosk = this.settingsStoreSelector.deviceIsKiosk;
  protected readonly isMobile = this.settingsStoreSelector.deviceIsMobileScreen;
  protected readonly deviceIsKiosk = this.settingsStoreSelector.isKioskLayout;
  private readonly kioskGlobalSettingsService = inject(KioskGlobalSettingsService);
  private readonly cb2PreviewState = inject(Cb2PreviewStateService);
  private readonly previewBlocKey = inject(CB2_PREVIEW_BLOC_KEY, { optional: true });

  protected readonly proposalButtonTextSize = computed(() => {
    if (this.isKiosk()) {
      return 'XL';
    }
    if (this.isDesktop()) {
      return 'L';
    }
    return 'L';
  });

  protected readonly index = toSignal(
    this.modalsService.getData('cross-selling-modal').pipe(map(el => (el?.index ? el.index : 4))),
    { initialValue: 4 },
  );

  readonly triggerProductId = computed(
    () => this.resolvedConfig()['trigger_product_id'] as unknown as string,
  );
  readonly products = computed(() =>
    resolveCrossSellingModalProducts({
      isPreview: this.cb2PreviewState.isActive(),
      scenarios: this.cb2PreviewState.previewScenarios(),
      blocKey: this.previewBlocKey,
      runtimeProducts: this.resolvedConfig()['products'] as unknown as DoodProductModel[],
    }),
  );
  readonly title = computed(() => this.resolvedConfig()['title'] as unknown as TranslationField);
  readonly description = computed(
    () => this.resolvedConfig()['description'] as unknown as TranslationField,
  );
  readonly colors = computed(
    () =>
      this.resolvedConfig()['colors'] as unknown as {
        background_color?: IContentBuilderFieldColor;
        heading_color?: IContentBuilderFieldColor;
        description_color?: IContentBuilderFieldColor;
        productCardColors?: {
          textColor?: IContentBuilderFieldColor;
          cardColor?: IContentBuilderFieldColor;
          buttonBackgroundColor?: IContentBuilderFieldColor;
          unavailableButtonBackground?: IContentBuilderFieldColor;
          unavailableButtonText?: IContentBuilderFieldColor;
          buttonTextColor?: IContentBuilderFieldColor;
        };
        card_color?: IContentBuilderFieldColor;
        close_icon_colors?: ContentBuilderFieldColors;
      },
  );
  readonly crossSellingRuleId = computed(
    () => this.resolvedConfig()['cross_selling_rule_id'] as unknown as string,
  );

  // Computed signals for easier access to nested color properties
  readonly backgroundColor = computed(() => this.colors()?.background_color);
  readonly headingColor = computed(() => this.colors()?.heading_color);
  readonly descriptionColor = computed(() => this.colors()?.description_color);
  readonly textColor = computed(() => this.colors()?.productCardColors?.textColor);
  readonly cardColor = computed(() => this.colors()?.productCardColors?.cardColor);
  readonly buttonBackgroundColor = computed(
    () => this.colors()?.productCardColors?.buttonBackgroundColor,
  );
  readonly unavailableButtonBackground = computed(
    () => this.colors()?.productCardColors?.unavailableButtonBackground,
  );
  readonly unavailableButtonText = computed(
    () => this.colors()?.productCardColors?.unavailableButtonText,
  );
  readonly buttonTextColor = computed(() => this.colors()?.productCardColors?.buttonTextColor);
  readonly cardColorTop = computed(() => this.colors()?.card_color);
  readonly closeIconFieldColors = computed(() => {
    const cfg = this.resolvedConfig() as Record<string, unknown>;
    return resolveCloseIconColors(cfg, this.config() as Record<string, unknown>);
  });

  protected readonly productsLayout = computed(() =>
    resolveCrossSellingProductsLayout({
      productCount: this.products()?.length ?? 0,
      productCardChoice: this.kioskGlobalSettingsService.productCardChoice(),
      showsKioskProductCard: !!this.deviceIsKiosk() || !this.isMobile(),
    }),
  );

  private readonly store = inject(Store);

  private selectProposal(product: DoodProductModel): void {
    this.store.dispatch(
      PRODUCT_STORE_COMPONENT_ACTIONS.selectProduct({
        productId: product.id,
        productShopId: product.shop_id ?? '',
        productOrigin: {
          channel: 'cross-sell',
          reference: `cross_sell_rule_${this.crossSellingRuleId()}`,
          trigger_product_id: this.triggerProductId(),
        },
      }),
    );
  }

  addProductDispatcher(product: DoodProductModel): void {
    this.selectProposal(product);
    this.close();
  }

  openProductModal(product: DoodProductModel): void {
    this.productDispatcher.insertActive(product);
    this.modalsService.open<KioskProductOverviewModalConfig>(
      KioskProductOverviewModalComponent.handle,
      undefined,
      {
        image: product.images?.cover?.url,
        name: product.name,
        description: product.description,
        allergens: product.allergens,
        type: product.type,
        zIndex: KIOSK_PRODUCT_OVERVIEW_ABOVE_SALES_BOOSTER_INDEX,
        colors: {
          background: this.buttonTextColor(),
          accent: this.buttonBackgroundColor(),
          closeButton: this.buttonBackgroundColor(),
        },
      },
    );
  }

  close(): void {
    this.modalsService.close(CrossSellingModalComponent.handle);
  }

  protected readonly unAvailableProductById = computed(() => {
    const errors = this.orderStoreSelector.selectErrorsSignal();
    return errors.violations
      ?.filter(
        violation =>
          violation.code &&
          [
            'PRODUCT_NOT_AVAILABLE_FOR_DISTRIBUTION_MODE',
            'PRODUCT_HIDDEN',
            'PRODUCT_NOT_AVAILABLE',
          ].includes(violation.code),
      )
      .map(violation => violation.data?.id as string)
      .reduce(
        (acc, id) => {
          acc[id] = true;
          return acc;
        },
        {} as Record<string, boolean>,
      );
  });
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(CrossSellingModalComponent, { 'keyAliases': {} });
